// ABOUTME: Custom hook for managing bulk selection state and logic
// ABOUTME: Separates selection logic from UI components for better maintainability

import { useCallback, useState } from 'react';

import type { ISharePointItem } from '../api/types';

export const useBulkSelection = () => {
    const [selectedItems, setSelectedItems] = useState<
        (ISharePointItem & { hasChildrenSelected?: boolean })[]
    >([]);

    // Helper functions for hierarchical relationships
    const hasDescendantSelected = useCallback(
        (item: ISharePointItem, allSelectedItems: ISharePointItem[]): boolean =>
            allSelectedItems.some(selectedItem => {
                // Check if selectedItem is a direct child
                if (selectedItem.parentReference?.id === item.driveItemId) {
                    return true;
                }
                // Check if selectedItem has this item in its breadcrumbs
                return selectedItem.breadcrumbs?.some(
                    breadcrumb => breadcrumb.driveItemId === item.driveItemId,
                );
            }),
        [],
    );

    const getDescendantItems = useCallback(
        (parentItem: ISharePointItem, allSelectedItems: ISharePointItem[]): ISharePointItem[] =>
            allSelectedItems.filter(selectedItem => {
                // Include direct children
                if (selectedItem.parentReference?.id === parentItem.driveItemId) {
                    return true;
                }
                // Include items that have this parent in their breadcrumbs
                return selectedItem.breadcrumbs?.some(
                    breadcrumb => breadcrumb.driveItemId === parentItem.driveItemId,
                );
            }),
        [],
    );

    const isItemSelected = useCallback(
        (item: ISharePointItem): boolean =>
            selectedItems.some(selectedItem => selectedItem.driveItemId === item.driveItemId),
        [selectedItems],
    );

    const isIndeterminate = useCallback(
        (item: ISharePointItem): boolean => {
            // Un item è indeterminate se non è selezionato ma ha discendenti selezionati
            const isDirectlySelected = selectedItems.some(
                selectedItem => selectedItem.driveItemId === item.driveItemId,
            );

            if (isDirectlySelected) {
                return false; // Se è selezionato direttamente, non può essere indeterminate
            }

            return hasDescendantSelected(item, selectedItems);
        },
        [selectedItems, hasDescendantSelected],
    );

    const handleSelectionChange = useCallback(
        (item: ISharePointItem, selected: boolean) => {
            setSelectedItems(prev => {
                const isCurrentlySelected = prev.some(
                    prevItem => prevItem.driveItemId === item.driveItemId,
                );
                const currentlyIndeterminate =
                    !isCurrentlySelected && hasDescendantSelected(item, prev);

                if (selected) {
                    if (currentlyIndeterminate) {
                        // Caso: item è indeterminate e viene cliccato per essere selezionato
                        // Deseleziona tutti i discendenti e seleziona solo questo item
                        const descendantItems = getDescendantItems(item, prev);
                        const withoutDescendants = prev.filter(
                            prevItem =>
                                !descendantItems.some(
                                    desc => desc.driveItemId === prevItem.driveItemId,
                                ),
                        );
                        return [...withoutDescendants, item];
                    }
                    // Aggiungi l'item alla selezione
                    const newSelection = [...prev, item];

                    // Ora controlla se questo item ha antenati selezionati che dovrebbero essere deselezionati
                    // perché ora hanno figli selezionati
                    return newSelection.filter(selectedItem => {
                        // Se è l'item appena aggiunto, mantienilo
                        if (selectedItem.driveItemId === item.driveItemId) {
                            return true;
                        }

                        // Controlla se questo selectedItem è un antenato dell'item appena selezionato
                        const isAncestorOfNewItem =
                            item.breadcrumbs?.some(
                                breadcrumb => breadcrumb.driveItemId === selectedItem.driveItemId,
                            ) || item.parentReference?.id === selectedItem.driveItemId;

                        // Se è un antenato, rimuovilo dalla selezione diretta
                        if (isAncestorOfNewItem) {
                            return false;
                        }

                        return true;
                    });
                }
                // Deseleziona l'item e tutti i suoi discendenti
                const descendantItems = getDescendantItems(item, prev);
                return prev.filter(prevItem => {
                    // Rimuovi l'item stesso
                    if (prevItem.driveItemId === item.driveItemId) {
                        return false;
                    }
                    // Rimuovi tutti i discendenti
                    return !descendantItems.some(desc => desc.driveItemId === prevItem.driveItemId);
                });
            });
        },
        [hasDescendantSelected, getDescendantItems, selectedItems.length],
    );

    const handleRemoveFromSummary = useCallback((itemToRemove: ISharePointItem) => {
        setSelectedItems(prev =>
            prev.filter(selectedItem => !(selectedItem.driveItemId === itemToRemove.driveItemId)),
        );
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedItems([]);
    }, []);

    return {
        selectedItems,
        isItemSelected,
        isIndeterminate,
        handleSelectionChange,
        handleRemoveFromSummary,
        clearSelection,
    };
};
